import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Chip } from "@mui/material";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";
import { fmtNyDateTime } from "./prepDates";
import ReviewBatchModal from "./ReviewBatchModal";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;
const authCfg = () => ({ headers: { Authorization: getAuthHeader() } });

// Review-before-send batches (Phase 4 §7). A REVIEW-mode schedule fire parks each student's
// draft in a batch; this surfaces the actionable ones as a single "Review N pending drafts"
// button that opens the full review modal (per-student tabs, editable summaries, email + PDF
// preview, Send). The inline expand/edit UI now lives entirely in that modal.
const EDITABLE = ["PENDING_REVIEW", "PARTIALLY_SENT"];

const ReportBatchesPanel = ({ programUuid }) => {
    const [batches, setBatches] = useState([]);
    const [openBatchId, setOpenBatchId] = useState(null);

    const loadBatches = useCallback(async () => {
        try {
            const res = await Axios.get(`${BASE}/${programUuid}/report-batches`, authCfg());
            setBatches(res.data || []);
        } catch (e) {
            setBatches([]);
        }
    }, [programUuid]);
    useEffect(() => { loadBatches(); }, [loadBatches]);

    if (!programUuid) return null;
    const actionable = batches.filter((b) => EDITABLE.includes(b.status));
    if (actionable.length === 0) return null;

    const pendingOf = (b) => (b.item_counts && b.item_counts.PENDING) || 0;

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Report batches (review &amp; send)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                When delivery is set to “hold for review”, each scheduled run parks its drafts here.
                Nothing reaches students or parents until you review and send.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
                {actionable.map((b) => (
                    <Button
                        key={b.id}
                        variant="outlined"
                        startIcon={<RateReviewOutlinedIcon />}
                        onClick={() => setOpenBatchId(b.id)}
                        sx={{ textTransform: "none", justifyContent: "flex-start" }}
                    >
                        Review {pendingOf(b)} pending draft{pendingOf(b) === 1 ? "" : "s"}
                        <Box component="span" sx={{ mx: 1, color: "text.secondary", fontWeight: 400 }}>·</Box>
                        <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>{b.scheduled_for}</Box>
                        {b.status === "PARTIALLY_SENT" && (
                            <Chip size="small" color="info" label="partially sent" sx={{ ml: 1, height: 18, ".MuiChip-label": { px: 0.75, fontSize: 11 } }} />
                        )}
                        {b.generated_at && (
                            <Box component="span" sx={{ ml: 1, color: "text.disabled", fontWeight: 400, fontSize: 12 }}>
                                generated {fmtNyDateTime(b.generated_at)}
                            </Box>
                        )}
                    </Button>
                ))}
            </Box>

            <ReviewBatchModal
                open={openBatchId != null}
                batchId={openBatchId}
                programUuid={programUuid}
                onClose={() => setOpenBatchId(null)}
                onChanged={loadBatches}
            />
        </Box>
    );
};

export default ReportBatchesPanel;
